import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { Service, Slack, ValidationErrors } from 'webapp-types';
import { DeliveryType } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';

import didUpdate from 'webapp/modifiers/did-update';
import FormEmailRecipientsComponent from 'webapp/components/form/email-recipients';
import NotificationsDeliveryTargetsComponent from 'webapp/components/notifications/delivery-targets';
import NotificationsEditFormRowComponent from 'webapp/components/notifications/edit/form/row';
import RowErrorComponent from 'webapp/components/row-error';

interface ModelWithEmailAndSlackRecipients {
	isNew: boolean;
	slackChannels: Slack.Channels;
	recipients: string[];
	errors: ValidationErrors<string>;
}

type ValidationKeys = 'recipients' | 'delivery';

interface Args {
	model: ModelWithEmailAndSlackRecipients;
	origin: string;
	submitInProgress: boolean;
	addSendToTextToLabels: boolean;
	onContentChange: () => void;
	errors?: ValidationErrors<'deliveryTypes' | 'recipients' | 'deliveryOrRecipients'>;
	theme?: string;
	onFocus?: () => void;
	onClose?: () => void;
}

export default class FormEmailAndSlackRecipientsComponent extends Component<Args> {
	@service('analytics') declare analyticsService: Service.Analytics;

	@tracked selectedDeliveryTypes: number[] = [];

	@tracked showLocalErrors = false; // show only after we've changed any properties here to avoid eagerly validating on e.g. clean form

	deliveryTypes = [DeliveryType.EMAIL, DeliveryType.SLACK];

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.setSelectedDeliveryTypes();
	}

	get isEmailSelected(): boolean {
		return this.selectedDeliveryTypes.includes(DeliveryType.EMAIL);
	}

	get isSlackSelected(): boolean {
		return this.selectedDeliveryTypes.includes(DeliveryType.SLACK);
	}

	// single source for the delivery row error state - multiple RowError components
	// on the same row would override each other's error class
	get deliveryErrorItems() {
		return [...(this.errors.delivery || []), ...(this.args.model.errors.deliveryTypes || [])];
	}

	get errors() {
		const errors: ValidationErrors<ValidationKeys> = {};

		if (this.args.errors?.deliveryTypes) {
			errors.delivery = this.args.errors.deliveryTypes;
		}
		if (this.args.errors?.recipients) {
			errors.recipients = this.args.errors.recipients;
		}

		const showLocalErrors = this.showLocalErrors || this.args.errors?.deliveryOrRecipients;

		if (showLocalErrors) {
			if (!this.isEmailSelected && !this.isSlackSelected) {
				errors.delivery = [{ message: 'Required' }];
			}

			if (this.isEmailSelected && !this.args.model.recipients.length) {
				errors.recipients = [{ message: 'Required' }];
			}

			if (!this.showLocalErrors) {
				next(this.turnOnLocalErrors);
			}
		}

		return errors;
	}

	setSelectedDeliveryTypes(): void {
		const model = this.args.model;

		// set selected delivery types
		const preselectedDeliveryTypes: number[] = [];

		if (model.slackChannels.length > 0) {
			preselectedDeliveryTypes.push(DeliveryType.SLACK);
		}

		if ((model.isNew && !preselectedDeliveryTypes.length) || model.recipients.length > 0) {
			preselectedDeliveryTypes.push(DeliveryType.EMAIL);
		}

		this.selectedDeliveryTypes = preselectedDeliveryTypes;
	}

	@action
	turnOnLocalErrors() {
		if (!this.showLocalErrors) {
			this.showLocalErrors = true;
		}
	}

	@action
	onModelChange(): void {
		this.setSelectedDeliveryTypes();
		this.showLocalErrors = false;
	}

	@action
	updateDelivery(value: number[]): void {
		this.selectedDeliveryTypes = value;

		if (!this.isEmailSelected) {
			this.updateRecipients([]);
		}
		if (!this.isSlackSelected) {
			this.updateSlackChannels([]);
		}

		this.turnOnLocalErrors();
	}

	@action
	updateSlackChannels(value: Slack.Channels): void {
		this.analyticsService.event(TrackingEvent.SLACK_CHANNELS_SELECTED, {
			origin: this.args.origin,
		});
		this.args.model.slackChannels = value;

		this.turnOnLocalErrors();
	}

	@action
	updateRecipients(value: string[]): void {
		this.args.model.recipients = value;
		next(() => {
			this.args.onContentChange();
		});

		this.turnOnLocalErrors();
	}
	<template>
		<NotificationsEditFormRowComponent @label="Delivery method" @className="row--checkboxes">
			<NotificationsDeliveryTargetsComponent
				@selected={{this.selectedDeliveryTypes}}
				@slackChannels={{@model.slackChannels}}
				@deliveryTypes={{this.deliveryTypes}}
				@setDefaults={{@model.isNew}}
				@isDisabled={{@submitInProgress}}
				@onSelectionChange={{this.updateDelivery}}
				@onSlackChannelsChange={{this.updateSlackChannels}}
				@isEmailEnabled={{true}}
				@hideLabel={{true}}
				@addSendToTextToLabels={{@addSendToTextToLabels}}
			/>
			<RowErrorComponent @items={{this.deliveryErrorItems}} />
			<span {{didUpdate this.onModelChange @model}}></span>
		</NotificationsEditFormRowComponent>

		{{#if this.isEmailSelected}}
			<FormEmailRecipientsComponent
				@model={{@model}}
				@errors={{this.errors}}
				@submitInProgress={{@submitInProgress}}
				@onContentChange={{@onContentChange}}
				@onFocus={{@onFocus}}
				@onClose={{@onClose}}
				@theme={{@theme}}
			/>
		{{/if}}
	</template>
}
